import {
  GenericBracket, PitBracketMessage,
  PitMessage,
  PitMessageType,
  PitOnlineURLMessage, PitPlayoffRank, PitRank,
  PitRankingMessage,
  PitResultMessage, PlayoffStructure,
  SeriesDescription
} from "../javaTypes";
import { useReducer, createContext } from "react";

function isRankingMessage(msg: PitMessage<unknown>): msg is PitRankingMessage<PitRank> {
  return msg.type === PitMessageType.RANKINGS || msg.type === PitMessageType.ALL_RANKINGS;
}

function isPlayoffRankingMessage(msg: PitMessage<unknown>): msg is PitRankingMessage<PitPlayoffRank> {
  return msg.type === PitMessageType.PLAYOFF_RANKINGS;
}

function isResultMessage(msg: PitMessage<unknown>): msg is PitResultMessage {
  return msg.type === PitMessageType.RESULT;
}

function isOnlineURLMessage(msg: PitMessage<unknown>): msg is PitOnlineURLMessage {
  return msg.type === PitMessageType.ONLINE_URL;
}

function isBracketMessage(msg: PitMessage<unknown>): msg is PitBracketMessage {
  return msg.type === PitMessageType.BRACKET;
}

interface RankingMatch {
  number: string,
  redScore: number,
  blueScore: number,
  redTeams: string,
  blueTeams: string,
  winner: string
}

interface RankingTeam {
  rank: string | number,
  number: string,
  rp: string,
  tbp1: string,
  tbp2: string,
  record: string,
  matchesPlayed: number,
  leagueRank: number | string
}

interface RankingAlliance {
  rank: string | number,
  seed: number,
  displaySeed: string,
  teams: string[],
  rp: string,
  tbp1: string,
  tbp2: string,
  matchesPlayed: number,
  record: string,
}

export interface DivisionRankingData {
  matchList: RankingMatch[],
  matchCount: number,
  matchesPlayed: number,
  matchesPerTeam: number,
  lastUpdatedAt: number,
  rankings: RankingTeam[],
  allRankings?: RankingTeam[],
  playoffRankings?: RankingAlliance[],
  url: string | null,
  inElims: boolean,
  bracket?: GenericBracket,
  playoffStructure?: PlayoffStructure<SeriesDescription>
}

const placeholderRankingData: DivisionRankingData = {
  matchList: [],
  matchCount: 0,
  matchesPlayed: 0,
  matchesPerTeam: 0,
  lastUpdatedAt: 0,
  rankings: [],
  allRankings: undefined,
  playoffRankings: undefined,
  url: null,
  inElims: false,
  bracket: undefined
}

export function reducer(state : Record<number, DivisionRankingData>, msg : PitMessage<unknown>) {
  const divData = Object.assign({}, placeholderRankingData, state[msg.division]);
  if (isRankingMessage(msg)) {
    Object.assign<DivisionRankingData, Partial<DivisionRankingData>>(divData, {
      [msg.type === PitMessageType.ALL_RANKINGS ? 'allRankings' : 'rankings']:
        msg.payload.map((res) : RankingTeam => ({
          rank: res.rank > 0 ? res.rank : (res.rank === 0 ? '-' : 'NP'),
          number: res.team,
          rp: res.RP,
          tbp1: res.TBP1,
          tbp2: res.TBP2,
          record: `${res.wins}-${res.losses}-${res.ties}`,
          matchesPlayed: res.plays,
          leagueRank: res.leagueRank > 0 ? res.leagueRank : (res.leagueRank === 0 ? '-' : 'NP')
        }))
    })
  } else if (isPlayoffRankingMessage(msg)) {
    Object.assign<DivisionRankingData, Partial<DivisionRankingData>>(divData, {
      playoffRankings:
          msg.payload.map(res => ({
            rank: res.rank > 0 ? res.rank : (res.rank === 0 ? '-' : 'NP'),
            seed: res.seed,
            displaySeed: res.displaySeed,
            teams: res.team,
            rp: res.RP,
            tbp1: res.TBP1,
            tbp2: res.TBP2,
            record: `${res.wins}-${res.losses}-${res.ties}`,
            matchesPlayed: res.plays,
          }))
    })
  }else if (isResultMessage(msg)) {
    const results = msg.payload.filter((m) => m.played).map((m) => ({
      number: m.match,
      redScore: m.red,
      blueScore: m.blue,
      redTeams: m.redTeams.join("\n"),
      blueTeams: m.blueTeams.join("\n"),
      winner: m.winner
    }));
    Object.assign<DivisionRankingData, Partial<DivisionRankingData>>(divData, {
      matchList: results,
      matchCount: msg.payload.filter(m => m.match.startsWith("Q")).length,
      matchesPlayed: results.filter(r => r.number.startsWith("Q")).length,
      matchesPerTeam: msg.matchesPerTeam,
      lastUpdatedAt: msg.lastUpdatedAt
    })
  } else if (isOnlineURLMessage(msg)) {
    Object.assign<DivisionRankingData, Partial<DivisionRankingData>>(divData, {
      url: msg.payload[0]
    })
  } else if (isBracketMessage(msg)) {
    Object.assign<DivisionRankingData, Partial<DivisionRankingData>>(divData, {
      bracket: msg.payload[0],
      playoffStructure: msg.structure,
      inElims: msg.inElims
    })
  }
  return Object.assign({}, state, { [msg.division]: divData });
}

export const RankingData = createContext<Record<number, DivisionRankingData>>({})
export const useRankingState = () => useReducer(reducer, {})
